import React, { createContext, useContext, useState, useEffect } from 'react';

// Ключі для localStorage
const STORAGE_KEYS = {
  SELECTED_DOCUMENT: 'selectedDocument',
  DOCUMENT_POSITIONS: 'documentPositions',
  DOCUMENT_STATUS: 'documentStatus',
  DOCUMENT_TYPE: 'documentType'
};

// Створюємо контекст
const DocumentContext = createContext();

// Хук для використання контексту
export const useDocument = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocument повинен використовуватися всередині DocumentProvider');
  }
  return context;
};

// Провайдер контексту
export const DocumentProvider = ({ children }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentPositions, setDocumentPositions] = useState([]);
  const [documentStatus, setDocumentStatus] = useState('new'); // new, processing, processed, completed
  const [documentType, setDocumentType] = useState(''); // Тип документу

  // Завантаження даних з localStorage при ініціалізації
  useEffect(() => {
    try {
      const savedDocument = localStorage.getItem(STORAGE_KEYS.SELECTED_DOCUMENT);
      const savedPositions = localStorage.getItem(STORAGE_KEYS.DOCUMENT_POSITIONS);
      const savedStatus = localStorage.getItem(STORAGE_KEYS.DOCUMENT_STATUS);
      const savedType = localStorage.getItem(STORAGE_KEYS.DOCUMENT_TYPE);

      if (savedDocument) {
        setSelectedDocument(JSON.parse(savedDocument));
      }
      if (savedPositions) {
        setDocumentPositions(JSON.parse(savedPositions));
      }
      if (savedStatus) {
        setDocumentStatus(savedStatus);
      }
      if (savedType) {
        setDocumentType(savedType);
      }
    } catch (error) {      console.error('Помилка завантаження даних з localStorage:', error);
      // Очищуємо пошкоджені дані
      clearDocument();
    }
  }, []);
  // Збереження обраного документа
  const selectDocument = (document, positions = [], isProcessed = false) => {
    try {
      const documentData = {
        id: document.id || document.filename,
        filename: document.filename,
        created: document.created || document.ДокументДата || document.data?.ДокументДата, // Додаємо підтримку ДокументДата
        size: document.size,
        data: document.data,
        documentType: document.documentType || document.ДокументВид || '', // Додаємо тип документа
        selectedAt: new Date().toISOString(),
        isProcessed: isProcessed // Прапор для визначення, що документ вже оброблено
      };

      const docType = document.documentType || document.ДокументВид || '';
      // Визначаємо статус документа залежно від того, чи оброблено його
      const status = isProcessed ? 'processed' : 'processing';

      setSelectedDocument(documentData);
      setDocumentPositions(positions);
      setDocumentStatus(status);      setDocumentType(docType); // Зберігаємо тип документа

      // Зберігаємо в localStorage
      localStorage.setItem(STORAGE_KEYS.SELECTED_DOCUMENT, JSON.stringify(documentData));
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_POSITIONS, JSON.stringify(positions));
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_STATUS, status);
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_TYPE, docType);

      console.log('📄 Документ обрано:', documentData.filename, 'тип:', docType, 'дата:', documentData.created, 'з', positions.length, 'позиціями', isProcessed ? '(ОБРОБЛЕНО)' : '(НЕ ОБРОБЛЕНО)');
    } catch (error) {
      console.error('Ошибка сохранения документа:', error);
    }
  };

  // Оновлення позицій документа
  const updateDocumentPositions = (positions) => {
    try {
      setDocumentPositions(positions);
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_POSITIONS, JSON.stringify(positions));
      console.log('📋 Позиції документа оновлені:', positions.length, 'позицій');    } catch (error) {
      console.error('Помилка оновлення позицій:', error);
    }
  };

  // Оновлення однієї позиції
  const updatePosition = (positionIndex, updates) => {
    try {
      const updatedPositions = [...documentPositions];
      if (updatedPositions[positionIndex]) {
        updatedPositions[positionIndex] = {
          ...updatedPositions[positionIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
        updateDocumentPositions(updatedPositions);
        console.log('📝 Позицію оновлено:', positionIndex, updates);
      }    } catch (error) {
      console.error('Помилка оновлення позиції:', error);
    }
  };

  // Оновлення статусу документа
  const updateDocumentStatus = (status) => {
    try {
      setDocumentStatus(status);
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_STATUS, status);
      console.log('📊 Статус документа изменен на:', status);
    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
    }
  };

  // Встановлення типу документа
  const updateDocumentType = (type) => {
    try {
      setDocumentType(type);
      localStorage.setItem(STORAGE_KEYS.DOCUMENT_TYPE, type);
      console.log('📑 Тип документа установлен:', type);
    } catch (error) {
      console.error('Ошибка установки типа документа:', error);
    }
  };

  // Очищення обраного документа
  const clearDocument = () => {
    try {
      setSelectedDocument(null);
      setDocumentPositions([]);
      setDocumentStatus('new');
      setDocumentType('');

      localStorage.removeItem(STORAGE_KEYS.SELECTED_DOCUMENT);
      localStorage.removeItem(STORAGE_KEYS.DOCUMENT_POSITIONS);
      localStorage.removeItem(STORAGE_KEYS.DOCUMENT_STATUS);
      localStorage.removeItem(STORAGE_KEYS.DOCUMENT_TYPE);

      console.log('🗑️ Дані документа очищено');    } catch (error) {
      console.error('Помилка очищення даних:', error);
    }
  };

  // Отримання позиції за артикулом
  const getPositionByBarcode = (barcode) => {
    return documentPositions.find(pos =>
      pos.barcode === barcode ||
      pos.article === barcode ||
      pos.id === barcode
    );
  };

  // Поиск позиций по частичному совпадению
  const searchPositions = (searchTerm) => {
    if (!searchTerm) return documentPositions;

    const term = searchTerm.toLowerCase();
    return documentPositions.filter(pos =>
      (pos.article && pos.article.toLowerCase().includes(term)) ||
      (pos.name && pos.name.toLowerCase().includes(term)) ||
      (pos.barcode && pos.barcode.toLowerCase().includes(term))
    );
  };

  // Статистика по документу
  const getDocumentStats = () => {
    const total = documentPositions.length;
    const processed = documentPositions.filter(pos => pos.fact > 0).length;
    const completed = documentPositions.filter(pos => pos.fact >= pos.plan).length;
    const remaining = total - processed;

    return {
      total,
      processed,
      completed,
      remaining,
      completionPercent: total > 0 ? Math.round((completed / total) * 100) : 0,
      processedPercent: total > 0 ? Math.round((processed / total) * 100) : 0
    };
  };

  const value = {
    // Данные
    selectedDocument,
    documentPositions,
    documentStatus,
    documentType, // Добавлено в контекст

    // Методы управления документом
    selectDocument,
    clearDocument,
    updateDocumentStatus,
    updateDocumentType, // Переименовано из setDocumentType

    // Методы управления позициями
    updateDocumentPositions,
    updatePosition,
    getPositionByBarcode,
    searchPositions,

    // Статистика
    getDocumentStats,

    // Утилиты
    hasSelectedDocument: !!selectedDocument,
    isDocumentProcessing: documentStatus === 'processing',
    isDocumentCompleted: documentStatus === 'completed'
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};

export default DocumentContext;
